import {AttributesRow, Flags} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, indexBy, isEmpty, isNil, isNotEmpty} from 'ramda';
import {isArray, isBoolean, isString, isTrue} from 'ramda-adjunct';

import {
  TenantFieldDefinitionResponseBody,
  useGetCustomFieldsPayloadQuery,
  useGetFieldDefinitionListQuery,
} from '@dms/api/settings';
import {EntityResourceIds, FieldValue, TypedAnyValue} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {CurrencyCodeType, parseDate} from 'shared/web';

import {isCurrency} from '../utils/isCurrency';

type CustomFieldsParameterRowProps = {
  resourceId: EntityResourceIds;
  recordId: string;
};

const getBooleanString = (value: unknown) =>
  isBoolean(value)
    ? i18n.t(`general.labels.${value ? 'yes' : 'no'}`)
    : i18n.t('general.labels.empty');

const getEnumString = (
  value: Required<TypedAnyValue['value']>,
  definition: TenantFieldDefinitionResponseBody | undefined
) => {
  if (!definition) {
    return null;
  }

  const enumOption = definition.enumOptions?.find((option) => option.uuid === value);
  return enumOption?.name ?? null;
};

export const useCustomFieldsParameterRow = (props: CustomFieldsParameterRowProps) => {
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const {data: payload} = useGetCustomFieldsPayloadQuery(props);
  const {data: definitions} = useGetFieldDefinitionListQuery({
    resourceId: props.resourceId,
    type: 'tenant',
  });

  const payloadByDefinitionId = indexBy((item) => item.definitionId, payload ?? []);
  const definitionsById = indexBy((item) => item.id, definitions ?? []);

  const getFieldValue = (field: FieldValue): string | null => {
    const value = field?.value?.value;
    const type = field?.value?.type;

    if (isNil(value)) {
      return null;
    }

    const returnValue = match(type)
      .with('datetime', () =>
        isString(value) ? formatDateTime('dateTimeShort', parseDate(value)) : null
      )
      .with('date', () => (isString(value) ? formatDateTime('dateShort', parseDate(value)) : null))
      .with('boolean', always(getBooleanString(value)))
      .with('enum', () => getEnumString(value, definitionsById[field.definitionId]))
      .with(
        'money',
        always(
          isCurrency(value)
            ? formatCurrency(Number(value.amount), value.currency as CurrencyCodeType)
            : null
        )
      )
      .otherwise(always(value.toString()));

    return returnValue;
  };

  const getFieldContent = (
    field: FieldValue,
    definition: TenantFieldDefinitionResponseBody
  ): React.ReactNode => {
    const value = field?.value?.value;

    if (!isArray(value)) {
      return null;
    }

    const flags = value
      ?.map((item: string) => ({
        label: definition?.enumOptions?.find((option) => option.uuid === item)?.name ?? '',
      }))
      .filter((item) => isNotEmpty(item.label));

    if (isEmpty(flags)) {
      return null;
    }

    return <Flags flags={flags} colorScheme="blue" isSubtle size="small" shouldWrap />;
  };

  const hasUnfilledRequiredFields = definitions?.some(
    (definition) => isTrue(definition.isRequired) && !payloadByDefinitionId[definition.id]
  );

  const rows: AttributesRow[] =
    definitions?.map((definition) => {
      const definitionValue = payloadByDefinitionId[definition.id];

      if (definition.valueType === 'multi_enum') {
        return {
          label: definition.name,
          content: getFieldContent(definitionValue, definition),
        };
      }

      return {
        label: definition.name,
        value: getFieldValue(definitionValue),
      };
    }) ?? [];

  return [rows, hasUnfilledRequiredFields] as const;
};
